import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Pause, Volume2 } from 'lucide-react';

import { assistantsService } from '../../services/api/assistants';

interface Slide {
  titre: string;
  puces: string[];
  notes?: string;
}

interface Props {
  titre?: string;
  slides: Slide[];
  conversationId?: number;
  messageId?: number;
}

// Mêmes couleurs et mêmes proportions que le fichier produit : l'aperçu doit
// montrer ce que l'utilisateur obtiendra, pas une approximation.
const BLEU = '#1E4A4A';
// Bandeau de titre : 2,6 cm sur les 19,05 cm de hauteur du format large.
const HAUTEUR_BANDEAU = '13.6%';

/**
 * Aperçu d'une présentation, à l'image du fichier exporté.
 *
 * Affichait auparavant une fiche par diapositive — titre, puces et notes en
 * liste. On y lisait le contenu sans jamais voir la diapositive : impossible de
 * juger si le texte tenait, si le titre débordait, si la densité convenait.
 *
 * On rend donc la diapositive elle-même, au format large et aux couleurs de la
 * charte, avec la narration présentée à part comme ce qu'elle est — le texte
 * dit, non le texte projeté.
 */
const ApercuPresentation: React.FC<Props> = ({ titre, slides, conversationId, messageId }) => {
  const [courante, setCourante] = useState(0);
  const [chargement, setChargement] = useState<number | null>(null);
  const [lecture, setLecture] = useState<number | null>(null);
  const [erreur, setErreur] = useState('');
  const audio = useRef<HTMLAudioElement | null>(null);
  // Les pistes déjà synthétisées sont gardées : réécouter ne doit pas
  // relancer un appel facturé.
  const cache = useRef<Record<number, string>>({});

  useEffect(() => {
    const pistes = cache.current;
    return () => {
      audio.current?.pause();
      Object.values(pistes).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const ecouter = async (index: number) => {
    if (!conversationId || !messageId) return;

    if (lecture === index) {
      audio.current?.pause();
      setLecture(null);
      return;
    }

    audio.current?.pause();
    setErreur('');

    try {
      let url = cache.current[index];
      if (!url) {
        setChargement(index);
        const blob = await assistantsService.narrationSlide(conversationId, messageId, index);
        url = URL.createObjectURL(blob);
        cache.current[index] = url;
      }
      const lecteur = new Audio(url);
      lecteur.onended = () => setLecture(null);
      audio.current = lecteur;
      await lecteur.play();
      setLecture(index);
    } catch (e: any) {
      setErreur(e.message || "La narration n'a pas pu être générée.");
    } finally {
      setChargement(null);
    }
  };

  // L'export ajoute une diapositive de couverture. L'aperçu la montre aussi,
  // sans quoi les deux ne compteraient pas le même nombre de diapositives.
  const vues: Array<{ couverture: boolean; slide?: Slide; rang: number }> = [
    ...(titre ? [{ couverture: true, rang: -1 }] : []),
    ...slides.map((slide, rang) => ({ couverture: false, slide, rang })),
  ];
  const vue = vues[courante];
  const ecoutable = Boolean(conversationId && messageId) && !vue.couverture;
  const aller = (pas: number) =>
    setCourante((i) => Math.min(vues.length - 1, Math.max(0, i + pas)));

  return (
    <div className="space-y-4">
      {erreur && (
        <p className="rounded-md bg-eg-accent/10 px-3 py-2 text-sm text-eg-accent">{erreur}</p>
      )}

      {/* La diapositive, au format large du vidéoprojecteur. */}
      <div
        className="relative w-full overflow-hidden rounded-md border border-[#DDE2DD] bg-eg-blanc "
        style={{ aspectRatio: '16 / 9' }}
      >
        {vue.couverture ? (
          <div
            className="flex h-full items-center justify-center px-[12%] text-center"
            style={{ backgroundColor: BLEU }}
          >
            <h3
              className="font-bold leading-tight text-eg-blanc"
              style={{ fontSize: 'clamp(1rem, 3.2vw, 2rem)' }}
            >
              {titre}
            </h3>
          </div>
        ) : (
        <>
        <div
          className="flex items-center px-[3.5%]"
          style={{ height: HAUTEUR_BANDEAU, backgroundColor: BLEU }}
        >
          <h3 className="truncate font-bold text-eg-blanc" style={{ fontSize: 'clamp(0.75rem, 2.2vw, 1.4rem)' }}>
            {vue.slide!.titre}
          </h3>
        </div>

        <ul className="space-y-[2.2%] px-[6%] pt-[5%]">
          {vue.slide!.puces.map((puce, i) => (
            <li key={i} className="flex gap-[1.5%]">
              <span
                className="mt-[0.6em] h-[0.35em] w-[0.35em] shrink-0 rounded-full"
                style={{ backgroundColor: BLEU, fontSize: 'clamp(0.7rem, 1.5vw, 1rem)' }}
              />
              <span
                className="leading-snug text-eg-encre"
                style={{ fontSize: 'clamp(0.7rem, 1.5vw, 1rem)' }}
              >
                {puce}
              </span>
            </li>
          ))}
        </ul>
        </>
        )}

        <span className={`absolute bottom-[3%] left-1/2 -translate-x-1/2 text-xs ${
          vue.couverture ? 'text-eg-blanc/60' : 'text-eg-estompe'
        }`}>
          {courante + 1} / {vues.length}
        </span>
      </div>

      {/* Défilement : flèches et pellicule des vignettes. */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => aller(-1)}
          disabled={courante === 0}
          className="shrink-0 rounded-md border border-[#DDE2DD] p-2 text-eg-gris
            transition-colors hover:bg-eg-papier disabled:opacity-40"
          aria-label="Diapositive précédente"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex flex-1 gap-2 overflow-x-auto pb-1">
          {vues.map((v, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCourante(index)}
              className={`shrink-0 overflow-hidden rounded border text-left transition-colors ${
                index === courante
                  ? 'border-eg-accent ring-1 ring-eg-accent'
                  : 'border-eg-bord hover:border-[#C9D0C9]'
              }`}
              style={{ width: '5.5rem', aspectRatio: '16 / 9' }}
              title={v.couverture ? titre : v.slide!.titre}
            >
              {v.couverture ? (
                <div className="h-full w-full" style={{ backgroundColor: BLEU }} />
              ) : (
                <>
                  <div style={{ height: HAUTEUR_BANDEAU, backgroundColor: BLEU }} />
                  <div className="space-y-[2px] p-1">
                    {v.slide!.puces.slice(0, 3).map((_, i) => (
                      <div key={i} className="h-[2px] rounded bg-eg-bord" />
                    ))}
                  </div>
                </>
              )}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => aller(1)}
          disabled={courante === vues.length - 1}
          className="shrink-0 rounded-md border border-[#DDE2DD] p-2 text-eg-gris
            transition-colors hover:bg-eg-papier disabled:opacity-40"
          aria-label="Diapositive suivante"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* La narration n'est pas projetée : elle est dite. D'où sa mise à part. */}
      {!vue.couverture && (vue.slide!.notes || ecoutable) && (
        <div className="rounded-md border border-eg-bord bg-eg-papier p-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-xs font-medium uppercase tracking-wide text-eg-gris">
              Narration — ce qui est dit, non projeté
            </span>
            {ecoutable && (
              <button
                type="button"
                onClick={() => ecouter(vue.rang)}
                disabled={chargement !== null}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-eg-voile
                  px-2.5 py-1 text-xs font-medium text-eg-accent transition-colors
                  hover:bg-eg-voile disabled:opacity-50"
              >
                {chargement === vue.rang ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : lecture === vue.rang ? (
                  <Pause className="h-3.5 w-3.5" />
                ) : (
                  <Volume2 className="h-3.5 w-3.5" />
                )}
                {lecture === vue.rang ? 'Pause' : 'Écouter'}
              </button>
            )}
          </div>
          <p className="text-sm italic text-eg-gris">
            {vue.slide!.notes || 'Aucune narration pour cette diapositive.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ApercuPresentation;
